/**
 * Chunk Loading Error Handler
 *
 * Handles ChunkLoadError that occurs when users have cached HTML
 * but JavaScript chunks have been deleted after a new deployment.
 *
 * Features:
 * - Catches chunk loading errors globally and in Vue
 * - Shows user-friendly toast notification
 * - Automatically reloads the page after a brief delay
 * - Prevents infinite reload loops using sessionStorage
 */

const CHUNK_ERROR_KEY = 'chunk-reload-attempted'
const CHUNK_ERROR_TIMESTAMP_KEY = 'chunk-reload-timestamp'
const CHUNK_ERROR_COUNT_KEY = 'chunk-reload-count'
const RELOAD_DELAY = 2000 // 2 seconds
const RELOAD_COOLDOWN = 60000 // 60 seconds - prevent reload loops
const MAX_RELOAD_ATTEMPTS = 3 // Maximum consecutive reload attempts

/**
 * Check if error is a chunk loading error
 */
function isChunkLoadError (error) {
  if (!error) return false

  const message = error.message || ''
  const name = error.name || ''

  // Check for various chunk loading error patterns
  return (
    name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  )
}

/**
 * Get reload attempt count
 */
function getReloadCount () {
  try {
    const count = sessionStorage.getItem(CHUNK_ERROR_COUNT_KEY)
    return count ? parseInt(count, 10) : 0
  } catch {
    return 0
  }
}

/**
 * Check if we should reload (not in cooldown period and not over max attempts)
 */
function shouldAttemptReload () {
  try {
    const lastAttempt = sessionStorage.getItem(CHUNK_ERROR_TIMESTAMP_KEY)
    const reloadCount = getReloadCount()

    // Check if we've exceeded max attempts
    if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
      console.error(`Chunk reload loop detected: ${reloadCount} consecutive attempts. Stopping to prevent infinite loop.`)
      return false
    }

    if (!lastAttempt) {
      return true
    }

    const timeSinceLastAttempt = Date.now() - parseInt(lastAttempt, 10)

    // If enough time has passed, reset the counter
    if (timeSinceLastAttempt > RELOAD_COOLDOWN) {
      return true
    }

    console.error(`Chunk load error in cooldown period. Time since last attempt: ${Math.round(timeSinceLastAttempt / 1000)}s`)
    return false
  } catch {
    // If sessionStorage is not available, allow reload
    return true
  }
}

/**
 * Mark that we've attempted a reload
 */
function markReloadAttempted () {
  try {
    const currentCount = getReloadCount()
    sessionStorage.setItem(CHUNK_ERROR_KEY, 'true')
    sessionStorage.setItem(CHUNK_ERROR_TIMESTAMP_KEY, Date.now().toString())
    sessionStorage.setItem(CHUNK_ERROR_COUNT_KEY, (currentCount + 1).toString())

    console.log(`Marking chunk reload attempt ${currentCount + 1}/${MAX_RELOAD_ATTEMPTS}`)
  } catch {
    // sessionStorage not available, continue anyway
    console.warn('Could not access sessionStorage for chunk error handling')
  }
}

/**
 * Clear reload attempt flag only if enough time has passed
 * This prevents clearing the protection during an active reload loop
 */
function clearReloadFlag () {
  try {
    const lastAttempt = sessionStorage.getItem(CHUNK_ERROR_TIMESTAMP_KEY)

    if (!lastAttempt) {
      // No previous attempt, nothing to clear
      return
    }

    const timeSinceLastAttempt = Date.now() - parseInt(lastAttempt, 10)

    // Only clear if we're outside the cooldown period
    // This means the page loaded successfully and enough time has passed
    if (timeSinceLastAttempt > RELOAD_COOLDOWN) {
      console.log('Clearing chunk reload flags - cooldown period passed')
      sessionStorage.removeItem(CHUNK_ERROR_KEY)
      sessionStorage.removeItem(CHUNK_ERROR_TIMESTAMP_KEY)
      sessionStorage.removeItem(CHUNK_ERROR_COUNT_KEY)
    } else {
      console.log(`Preserving chunk reload protection (${Math.round(timeSinceLastAttempt / 1000)}s since last attempt)`)
    }
  } catch {
    // sessionStorage not available
  }
}

/**
 * Handle chunk loading error with reload
 */
async function handleChunkLoadError (toast, bugsnag, deleteRuntimeCache) {
  const reloadCount = getReloadCount()

  if (!shouldAttemptReload()) {
    console.error('Chunk load error detected but cannot reload.')

    // Show error message to user if we've hit the limit
    if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
      if (toast) {
        toast('Unable to load the latest version. Please manually refresh the page.', 'error', {
          timeout: 10000
        })
      }

      // Log the infinite loop to bugsnag
      if (bugsnag) {
        bugsnag(new Error('Chunk loading infinite loop detected'), {
          severity: 'error',
          metadata: {
            reloadCount,
            reason: 'Hit maximum reload attempts',
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString()
          }
        })
      }
    }

    return false
  }

  // Log to bugsnag for monitoring
  if (bugsnag) {
    bugsnag(new Error('Chunk loading error - reloading page'), {
      severity: 'info',
      metadata: {
        reloadCount: reloadCount + 1,
        maxAttempts: MAX_RELOAD_ATTEMPTS,
        reason: 'Automatic reload due to chunk loading failure',
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      }
    })
  }

  // Show toast notification
  if (toast) {
    toast('Updating to latest version...', 'info', {
      timeout: RELOAD_DELAY
    })
  }

  // Mark that we're attempting a reload
  markReloadAttempted()

  // Delete service worker runtime cache before reloading
  // This is critical to ensure cached chunks are cleared
  if (deleteRuntimeCache) {
    try {
      await deleteRuntimeCache()
      console.log('Deleted runtime cache before reload')
    } catch (e) {
      console.error('Failed to delete runtime cache:', e)
    }
  }

  // Reload after delay
  setTimeout(() => {
    window.location.reload()
  }, RELOAD_DELAY)

  return true
}

/**
 * Install chunk loading error handler
 */
export const chunkLoadErrorNuxtInstall = (nuxtApp) => {
  const { $toast, $bugsnag } = nuxtApp.vueApp.config.globalProperties

  // Import deleteRuntimeCache from service worker composable
  let deleteRuntimeCache
  try {
    // Dynamic import to avoid circular dependencies
    import('@/composables/useServiceWorker.js').then((module) => {
      deleteRuntimeCache = module.useServiceWorker().deleteRuntimeCache
    })
  } catch (e) {
    console.warn('Could not load service worker composable:', e)
  }

  // Clear reload flag on successful page load
  clearReloadFlag()

  // Global error handler for uncaught chunk loading errors
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error)) {
      event.preventDefault() // Prevent default error handling

      console.log('Chunk loading error detected in global error handler')
      handleChunkLoadError($toast, $bugsnag, deleteRuntimeCache).catch(console.error)
    }
  })

  // Handle unhandled promise rejections (for dynamic imports)
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault() // Prevent default error handling

      console.log('Chunk loading error detected in unhandled rejection')
      handleChunkLoadError($toast, $bugsnag, deleteRuntimeCache).catch(console.error)
    }
  })

  // Extend Vue's error handler to catch chunk errors in components
  const originalErrorHandler = nuxtApp.vueApp.config.errorHandler

  nuxtApp.vueApp.config.errorHandler = async (error, instance, info) => {
    if (isChunkLoadError(error)) {
      console.log('Chunk loading error detected in Vue error handler')
      const handled = await handleChunkLoadError($toast, $bugsnag, deleteRuntimeCache)

      // If we handled it with a reload, don't propagate to original handler
      if (handled) {
        return
      }
    }

    // Call original error handler for non-chunk errors
    if (originalErrorHandler) {
      originalErrorHandler(error, instance, info)
    }
  }
}